import React, { createContext, useContext, useState, ReactNode, useCallback } from 'react';
import { commentAPI } from '../api';
import { AxiosError } from 'axios';

// 댓글 타입 정의
export type Comment = {
  id: number;
  postId: number;
  userId: number;
  username: string;
  content: string;
  parentId: number | null;
  isSecret: boolean;
  createdAt: string;
  updatedAt: string;
  replies?: Comment[];
  isDeleted?: boolean;
  isLiked?: boolean;
  likeCount?: number;
};

// 댓글 컨텍스트 타입 정의
type CommentContextType = {
  comments: Comment[];
  loading: boolean;
  error: string | null;
  getCommentsByPostId: (postId: number) => Promise<void>;
  createComment: (postId: number, content: string, parentId?: number, isSecret?: boolean) => Promise<void>;
  updateComment: (id: number, content: string, isSecret?: boolean) => Promise<void>;
  deleteComment: (id: number) => Promise<void>;
  likeComment: (id: number) => Promise<void>;
  unlikeComment: (id: number) => Promise<void>;
};

// 댓글 컨텍스트 생성
const CommentContext = createContext<CommentContextType | undefined>(undefined);

// 댓글 컨텍스트 프로바이더 타입
type CommentProviderProps = {
  children: ReactNode;
};

// 댓글 컨텍스트 프로바이더 구현
export const CommentProvider: React.FC<CommentProviderProps> = ({ children }) => {
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // 댓글 상태 업데이트 헬퍼 함수
  const updateCommentState = useCallback((updatedComment: Comment, isDelete: boolean = false) => {
    setComments(prevComments => {
      return prevComments.map(comment => {
        // 현재 댓글이 업데이트된 댓글인 경우
        if (comment.id === updatedComment.id) {
          return isDelete ? { ...comment, isDeleted: true, content: "삭제된 댓글입니다." } : updatedComment;
        }
        
        // // 자식 댓글이 있는지 확인하고 재귀적으로 처리
        // if (comment.children && comment.children.length > 0) {
        //   return {
        //     ...comment,
        //     children: comment.children.map(child => {
        //       if (child.id === updatedComment.id) {
        //         return isDelete ? { ...child, isDeleted: true, content: "삭제된 댓글입니다." } : updatedComment;
        //       }
        //       return child;
        //     })
        //   };
        // }
        
        return comment;
      });
    });
  }, []);

  // 게시물의 댓글 목록 조회
  const getCommentsByPostId = useCallback(async (postId: number) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await commentAPI.getCommentsByPostId(postId);
      
      // 댓글 계층 구조 구성
      const commentsData = response.data.comments;
      const parentComments: Comment[] = [];
      const childComments: { [key: number]: Comment[] } = {};
      
      // 부모 댓글과 자식 댓글 분류
      // commentsData.forEach((comment: Comment) => {
      //   if (comment.parentId === null) {
      //     parentComments.push({ ...comment, : [] });
      //   } else {
      //     if (!childComments[comment.parentId]) {
      //       childComments[comment.parentId] = [];
      //     }
      //     childComments[comment.parentId].push(comment);
      //   }
      // });
      
      // 자식 댓글을 부모 댓글에 연결
      // parentComments.forEach(comment => {
      //   if (childComments[comment.id]) {
      //     comment.children = childComments[comment.id];
      //   }
      // });
      
      setComments(commentsData);
    } catch (err) {
      const axiosError = err as AxiosError;
      setError(axiosError.message || '댓글을 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  // 댓글 작성
  const createComment = useCallback(async (postId: number, content: string, parentId?: number, isSecret?: boolean) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await commentAPI.createComment(postId, content, parentId, isSecret);
      const newComment = response.data.data;
      
      // if (parentId) {
      //   // 대댓글인 경우
      //   setComments(prevComments => {
      //     return prevComments.map(comment => {
      //       if (comment.id === parentId) {
      //         return {
      //           ...comment,
      //           children: [...(comment.children || []), newComment]
      //         };
      //       }
      //       return comment;
      //     });
      //   });
      // } else {
      //   // 일반 댓글인 경우
      //   setComments(prevComments => [...prevComments, { ...newComment, children: [] }]);
      // }
    } catch (err) {
      const axiosError = err as AxiosError;
      setError(axiosError.message || '댓글 작성 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  // 댓글 수정
  const updateComment = useCallback(async (id: number, content: string, isSecret?: boolean) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await commentAPI.updateComment(id, content, isSecret);
      const updatedComment = response.data.data;
      updateCommentState(updatedComment);
    } catch (err) {
      const axiosError = err as AxiosError;
      setError(axiosError.message || '댓글 수정 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  // 댓글 삭제
  const deleteComment = useCallback(async (id: number) => {
    setLoading(true);
    setError(null);
    
    try {
      await commentAPI.deleteComment(id);
      
      // 대상 댓글 찾기
      const targetComment = comments.find(c => c.id === id) || 
        comments.flatMap(c => c.replies || []).find(c => c.id === id);
      
      if (targetComment) {
        updateCommentState({ ...targetComment, isDeleted: true }, true);
      }
    } catch (err) {
      const axiosError = err as AxiosError;
      setError(axiosError.message || '댓글 삭제 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  // 댓글 추천
  const likeComment = useCallback(async (id: number) => {
    setError(null);
    
    try {
      await commentAPI.likeComment(id);
      
      // 대상 댓글 찾기
      const targetComment = comments.find(c => c.id === id) || 
        comments.flatMap(c => c.replies || []).find(c => c.id === id);
      
      if (targetComment) {
        const updatedComment = { 
          ...targetComment, 
          isLiked: true,
          likeCount: (targetComment.likeCount || 0) + 1 
        };
        updateCommentState(updatedComment);
      }
    } catch (err) {
      const axiosError = err as AxiosError;
      setError(axiosError.message || '댓글 추천 중 오류가 발생했습니다.');
    }
  }, []);

  // 댓글 추천 취소
  const unlikeComment = useCallback(async (id: number) => {
    setError(null);
    
    try {
      await commentAPI.unlikeComment(id);
      
      // 대상 댓글 찾기
      const targetComment = comments.find(c => c.id === id) || 
        comments.flatMap(c => c.replies || []).find(c => c.id === id);
      
      if (targetComment) {
        const updatedComment = { 
          ...targetComment, 
          isLiked: false,
          likeCount: Math.max((targetComment.likeCount || 0) - 1, 0)
        };
        updateCommentState(updatedComment);
      }
    } catch (err) {
      const axiosError = err as AxiosError;
      setError(axiosError.message || '댓글 추천 취소 중 오류가 발생했습니다.');
    }
  }, []);

  const value = {
    comments,
    loading,
    error,
    getCommentsByPostId,
    createComment,
    updateComment,
    deleteComment,
    likeComment,
    unlikeComment
  };

  return (
    <CommentContext.Provider value={value}>
      {children}
    </CommentContext.Provider>
  );
};

// 커스텀 훅 생성
export const useComment = () => {
  const context = useContext(CommentContext);
  if (context === undefined) {
    throw new Error('useComment must be used within a CommentProvider');
  }
  return context;
}; 